import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmModal';
import FotoUpload from '../../components/FotoUpload';
import DocumentoUpload from '../../components/DocumentoUpload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTrash, faTimes, faHistory } from '@fortawesome/free-solid-svg-icons';
import { fetchAlunoById, updateAluno, deleteAluno, fetchHistoricoAluno, checkMatricula } from '../../services/alunoService';
import { extensaoService } from '../../services/extensaoService';
import { fotoService } from '../../services/fotoService';
import { useAuth } from '../../contexts/AuthContext';

// Função para converter data para o formato do input date (YYYY-MM-DD)
const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    // Se já está no formato correto (YYYY-MM-DD)
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
      return dateValue.split('T')[0]; // Remove horário se houver
    }
    
    // Converte outros formatos
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    
    // Retorna no formato YYYY-MM-DD
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.log('Erro ao formatar data para input:', dateValue, error);
    return '';
  }
};

// Função para converter campo boolean que pode vir como buffer do MySQL
const convertBooleanField = (value) => {
  if (value && typeof value === 'object' && value.type === 'Buffer') {
    return value.data[0] === 1;
  } else if (typeof value === 'number') {
    return value === 1;
  } else if (typeof value === 'boolean') {
    return value;
  }
  return false;
};

// Funções para aplicar máscaras
const applyMask = {
  cpf: (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers
      .slice(0, 11)
      .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
      .replace(/(\d{3})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4')
      .replace(/(\d{3})(\d{3})(\d{2})/, '$1.$2.$3')
      .replace(/(\d{3})(\d{2})/, '$1.$2');
  },
  
  telefone: (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return `(${numbers}`;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 2)})${numbers.slice(2)}`;
    } else if (numbers.length <= 10) {
      // Telefone fixo: (21)3106-3491
      return `(${numbers.slice(0, 2)})${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
    } else {
      // Celular: (21)98029-8201
      return `(${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  },
  
  cep: (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers
      .slice(0, 8)
      .replace(/(\d{5})(\d{3})/, '$1-$2')
      .replace(/(\d{5})(\d{1,2})/, '$1-$2');
  }
};

// Função para obter classes CSS do campo (com erro ou normal)
const getFieldClass = (fieldName, fieldErrors, baseClass = "w-full border rounded px-3 py-2") => {
  return fieldErrors && fieldErrors[fieldName]
    ? `${baseClass} border-red-500 focus:ring-red-400 bg-red-50`
    : `${baseClass} focus:outline-none focus:ring-2 focus:ring-green-400`;
};

const validate = (form) => {
  if (!form.nome.trim()) return 'O nome é obrigatório.';
  if (form.nome.length > 100) return 'O nome deve ter até 100 caracteres.';
  
  // Validação condicional baseada em ser estrangeiro ou não
  if (form.estrangeira) {
    if (!form.docEstrangeiro || !form.docEstrangeiro.trim()) return 'O documento estrangeiro é obrigatório.';
    if (form.docEstrangeiro.length > 50) return 'Documento estrangeiro deve ter até 50 caracteres.';
  } else {
    if (!form.cpf || !form.cpf.trim()) return 'O CPF é obrigatório.';
    if (!/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(form.cpf)) return 'CPF inválido.';
  }
  
  if (!form.dataNascimento) return 'Data de nascimento é obrigatória.';
  if (!form.naturalidade || !form.naturalidade.trim()) return 'Naturalidade é obrigatória.';
  if (form.naturalidade && (form.naturalidade.length < 2 || form.naturalidade.length > 100)) return 'Naturalidade deve ter entre 2 e 100 caracteres.';
  if (!form.extensao) return 'Extensão é obrigatória - sem extensão não é possível gerar matrícula.';
  if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) return 'Email inválido.';
  if (form.telefoneCelular && form.telefoneCelular.length > 15) return 'Telefone celular deve ter até 15 caracteres.';
  if (form.telefoneResidencial && form.telefoneResidencial.length > 15) return 'Telefone residencial deve ter até 15 caracteres.';
  if (form.telefoneComercial && form.telefoneComercial.length > 15) return 'Telefone comercial deve ter até 15 caracteres.';
  if (form.identidade && form.identidade.length > 20) return 'Identidade deve ter até 20 caracteres.';
  if (form.orgaoExpedidor && form.orgaoExpedidor.length > 30) return 'Órgão expedidor deve ter até 30 caracteres.';
  if (form.status && form.status.length > 30) return 'Status deve ter até 30 caracteres.';
  if (form.bairro && form.bairro.length > 50) return 'Bairro deve ter até 50 caracteres.';
  if (form.cidade && form.cidade.length > 100) return 'Cidade deve ter até 100 caracteres.';
  if (form.estado && form.estado.length > 2) return 'Estado deve ter até 2 caracteres.';
  if (form.cep && form.cep.length > 10) return 'CEP deve ter até 10 caracteres.';
  if (form.logradouro && form.logradouro.length > 100) return 'Logradouro deve ter até 100 caracteres.';
  if (form.numero && form.numero.length > 30) return 'Número deve ter até 30 caracteres.';
  if (form.complemento && form.complemento.length > 255) return 'Complemento deve ter até 255 caracteres.';
  if (form.nomeIgrejaCongrega && form.nomeIgrejaCongrega.length > 100) return 'Nome da igreja deve ter até 100 caracteres.';
  if (form.nomePastor && form.nomePastor.length > 100) return 'Nome do pastor deve ter até 100 caracteres.';
  if (form.pais && form.pais.length > 50) return 'País deve ter até 50 caracteres.';
  if (form.paisIgreja && form.paisIgreja.length > 50) return 'País da Igreja deve ter até 50 caracteres.';
  return '';
};

const AlunoEdit = () => {
  const { id } = useParams();
  const { user, hasPermission } = useAuth();
  const nivelAcesso = user?.nivelAcesso || user?.perfil?.nivelAcesso || user?.nivel_acesso || 0;
  
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Motivo de o aluno não ter carregado (ex.: 403 de outra extensão). Fica
  // separado de `error`, que some sozinho depois de alguns segundos.
  const [loadError, setLoadError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({}); // Novo estado para erros por campo
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [extensoes, setExtensoes] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [fotoFile, setFotoFile] = useState(null);
  const [currentFotoUrl, setCurrentFotoUrl] = useState(null);
  const [showHistorico, setShowHistorico] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historicoPagination, setHistoricoPagination] = useState(null);
  const [matriculaEmUso, setMatriculaEmUso] = useState(false);
  const [matriculaEmUsoNome, setMatriculaEmUsoNome] = useState('');
  const [checkingMatricula, setCheckingMatricula] = useState(false);
  const navigate = useNavigate();

  // Limpar mensagens de erro após 8 segundos APENAS se não houver erros de campo
  useEffect(() => {
    if (error && Object.keys(fieldErrors).length === 0) {
      // Só limpa automaticamente se for erro genérico (sem erros de campo)
      const timer = setTimeout(() => {
        setError('');
      }, 8000);
      return () => clearTimeout(timer);
    }
    // Se houver fieldErrors, NÃO limpar automaticamente - usuário precisa corrigir
  }, [error, fieldErrors]);

  // Scroll para o topo quando houver mensagens de feedback
  useEffect(() => {
    if (error || success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error, success]);

  useEffect(() => {
    const getAluno = async () => {
      try {
        const result = await fetchAlunoById(id);
        console.log('Resultado da busca do aluno:', result);
        
        if (result.success) {
          const data = result.data;
          console.log('Dados do aluno recebido:', data);
          
          // Converter campos booleanos de buffer para boolean
          const booleanFields = [
            'estrangeira', 'docComprovanteResidencia', 'docHistoricoEscolar', 
            'docIdentidade', 'docCpf', 'docFoto', 'docCertidaoCasamentoNascimento'
          ];
          
          booleanFields.forEach(field => {
            if (data[field] !== undefined) {
              data[field] = convertBooleanField(data[field]);
            }
          });
        
          // Formatar a data de nascimento para o input date
          if (data.dataNascimento) {
            data.dataNascimento = formatDateForInput(data.dataNascimento);
          }
          // Formatar a data de matrícula para o input date
          // Usar dataMatricula se existir, senão usar cadastradoEm como fallback
          if (data.dataMatricula) {
            data.dataMatricula = formatDateForInput(data.dataMatricula);
          } else if (data.cadastradoEm) {
            data.dataMatricula = formatDateForInput(data.cadastradoEm);
          }
          
          // Aplicar máscaras aos campos que já vêm com dados
          if (data.cpf) data.cpf = applyMask.cpf(data.cpf);
          if (data.telefoneCelular) data.telefoneCelular = applyMask.telefone(data.telefoneCelular);
          if (data.telefoneResidencial) data.telefoneResidencial = applyMask.telefone(data.telefoneResidencial);
          if (data.telefoneComercial) data.telefoneComercial = applyMask.telefone(data.telefoneComercial);
          if (data.cep) data.cep = applyMask.cep(data.cep);
          
          // Carregar URL da foto
          if (data.fotoUrl) {
            setCurrentFotoUrl(fotoService.getFotoUrl(id, data.fotoUrl));
          }
          
          setForm(data);
        } else {
          setLoadError(result.error || 'Erro ao carregar dados do aluno. Tente novamente.');
        }
      } catch (err) {
        console.error('Erro ao carregar dados do aluno:', err);
        const errorMessage = err.response?.data?.message || (err.response?.status === 404
          ? 'Aluno não encontrado.'
          : err.response?.status === 403
          ? 'Você não tem permissão para acessar este aluno.'
          : 'Erro ao carregar dados do aluno. Tente novamente.');
        setLoadError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    getAluno();
  }, [id]);

  // Carregar lista de extensões
  useEffect(() => {
    const loadLists = async () => {
      try {
        setLoadingLists(true);
        
        // Carregar extensões
        const extensoesResponse = await extensaoService.list({ limit: 1000 });
        setExtensoes(extensoesResponse.data || []);
        
      } catch (err) {
        console.error('Erro ao carregar listas:', err);
      } finally {
        setLoadingLists(false);
      }
    };
    
    loadLists();
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    
    let formattedValue = value;
    
    // Aplicar máscaras automaticamente baseadas no nome do campo
    if (type !== 'checkbox') {
      switch (name) {
        case 'cpf':
          formattedValue = applyMask.cpf(value);
          break;
        case 'telefoneCelular':
        case 'telefoneResidencial':
        case 'telefoneComercial':
        case 'telefoneIgreja':
          formattedValue = applyMask.telefone(value);
          break;
        case 'cep':
        case 'cepIgreja':
        case 'cepIgrejaEstrangeira':
          formattedValue = applyMask.cep(value);
          break;
        default:
          formattedValue = value;
      }
    }
    
    setForm({ ...form, [name]: type === 'checkbox' ? checked : formattedValue });
    setError('');
    // Limpar erro do campo específico quando o usuário editar
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: null });
    }
  };

  // Mapeamento de nomes de campos para labels amigáveis
  const campoLabels = {
    nome: 'Nome', cpf: 'CPF', email: 'E-mail', dataNascimento: 'Data de Nascimento',
    telefoneCelular: 'Celular', telefoneResidencial: 'Tel. Residencial', telefoneComercial: 'Tel. Comercial',
    naturalidade: 'Naturalidade', nacionalidade: 'Nacionalidade', sexo: 'Sexo',
    estadoCivil: 'Estado Civil', identidade: 'Identidade', orgaoExpedidor: 'Órgão Expedidor',
    logradouro: 'Logradouro', numero: 'Número', complemento: 'Complemento',
    bairro: 'Bairro', cidade: 'Cidade', estado: 'Estado', cep: 'CEP', pais: 'País',
    nomeIgrejaCongrega: 'Igreja que Congrega', nomePastor: 'Pastor',
    cargo: 'Cargo', funcao: 'Função', status: 'Status', extensao: 'Extensão',
    curso: 'Curso', periodo: 'Período', tipoCurso: 'Tipo de Curso',
    matricula: 'Matrícula', fotoUrl: 'Foto',
    estrangeira: 'Estrangeiro', docEstrangeiro: 'Doc. Estrangeiro',
    docComprovanteResidencia: 'Comprovante Residência', docHistoricoEscolar: 'Histórico Escolar',
    docIdentidade: 'Doc. Identidade', docCertidaoCasamentoNascimento: 'Certidão Casamento/Nascimento',
    paisIgreja: 'País da Igreja', estadoIgreja: 'Estado da Igreja', cidadeIgreja: 'Cidade da Igreja',
    cepIgreja: 'CEP da Igreja', bairroIgreja: 'Bairro da Igreja',
    logradouroIgreja: 'Logradouro da Igreja', numeroIgreja: 'Número da Igreja',
  };

  const loadHistorico = async (page = 1) => {
    setHistoricoLoading(true);
    try {
      const result = await fetchHistoricoAluno(id, { page, limit: 10 });
      if (result.success) {
        setHistorico(result.data.historico || []);
        setHistoricoPagination(result.data.pagination || null);
      } else {
        setHistorico([]);
      }
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
      setHistorico([]);
    } finally {
      setHistoricoLoading(false);
    }
  };

  const handleOpenHistorico = () => {
    setShowHistorico(true);
    loadHistorico(1);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    // Removido validação frontend - deixar apenas backend validar para exibir erros nos campos
    // const validationError = validate(form);
    // if (validationError) {
    //   setError(validationError);
    //   return;
    // }
    if (matriculaEmUso) {
      setError(`A matrícula informada já está em uso pelo aluno: ${matriculaEmUsoNome}. Corrija antes de salvar.`);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(''); // Limpar erros anteriores
    setFieldErrors({}); // Limpar erros de campo anteriores
    try {
      // Preparar dados condicionalmente baseado no campo estrangeira
      const dadosParaEnvio = { ...form };
      
      if (form.estrangeira) {
        // Se é estrangeiro, garantir que CPF seja null
        dadosParaEnvio.cpf = null;
      } else {
        // Se não é estrangeiro, garantir que documento estrangeiro seja null
        dadosParaEnvio.docEstrangeiro = null;
      }
      
      const result = await updateAluno(id, dadosParaEnvio);
      
      if (result.success) {
        // Se tem foto nova, fazer upload
        if (fotoFile) {
          try {
            await fotoService.uploadFoto(id, fotoFile);
          } catch (fotoError) {
            console.error('Erro ao fazer upload da foto:', fotoError);
            // Não bloqueamos o sucesso da atualização por erro na foto
          }
        }
        
        setSuccess(true);
        setSuccessMessage(result.message || `Aluno "${form.nome}" foi atualizado com sucesso!`);
        setTimeout(() => navigate('/alunos'), 2000);
      } else {
        // Processar erros de validação do backend
        if (result.validationErrors && result.validationErrors.length > 0) {
          const errors = {};
          result.validationErrors.forEach(err => {
            // Se já existe um erro para esse campo, mantém o primeiro
            if (err.path && !errors[err.path]) {
              errors[err.path] = err.msg;
            }
          });
          setFieldErrors(errors);
          // Mensagem amigável indicando que há campos a corrigir
          setError('Por favor, corrija os campos destacados em vermelho.');
          
          // Scroll para o primeiro campo com erro
          setTimeout(() => {
            const firstErrorField = document.querySelector('.border-red-500');
            if (firstErrorField) {
              firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
              firstErrorField.focus();
            }
          }, 100);
        } else {
          setError(result.error || 'Erro ao atualizar aluno');
        }
      }
    } catch (err) {
      console.error('Erro ao atualizar aluno:', err);
      
      // Processar erros de validação do backend (vem no response.data)
      const errorData = err.response?.data;
      
      if (errorData?.validationErrors && Array.isArray(errorData.validationErrors) && errorData.validationErrors.length > 0) {
        const errors = {};
        errorData.validationErrors.forEach(validationErr => {
          // Se já existe um erro para esse campo, mantém o primeiro (mais relevante)
          if (validationErr.path && !errors[validationErr.path]) {
            errors[validationErr.path] = validationErr.msg;
          }
        });
        setFieldErrors(errors);
        setError('Por favor, corrija os campos destacados em vermelho.');
        
        // Scroll para o primeiro campo com erro
        setTimeout(() => {
          const firstErrorField = document.querySelector('.border-red-500');
          if (firstErrorField) {
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstErrorField.focus();
          }
        }, 100);
      } else {
        // Erro genérico
        const errorMessage = err.response?.status === 404 
          ? 'Aluno não encontrado.' 
          : err.response?.status === 403 
          ? 'Você não tem permissão para editar este aluno.'
          : errorData?.message || errorData?.error || 'Erro ao atualizar aluno. Tente novamente.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setError(''); // Limpar erros anteriores
    try {
      const result = await deleteAluno(id);
      
      if (result.success) {
        setDeleteSuccess(true);
        setTimeout(() => navigate('/alunos'), 2000);
      } else {
        setError(result.error || 'Erro ao excluir aluno');
      }
    } catch (err) {
      console.error('Erro ao excluir aluno:', err);
      const errorMessage = err.response?.status === 404 
        ? 'Aluno não encontrado.' 
        : err.response?.status === 403 
        ? 'Você não tem permissão para excluir este aluno.'
        : err.response?.status === 409
        ? 'Não é possível excluir este aluno pois existem registros dependentes.'
        : 'Erro ao excluir aluno. Tente novamente.';
      setError(errorMessage);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) return (
    <div className="max-w-lg mx-auto mt-10 p-6 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
        <span className="text-blue-700 font-medium">Carregando dados do aluno...</span>
      </div>
    </div>
  );
  
  if (deleteSuccess) return (
    <div className="max-w-lg mx-auto mt-10 p-6 bg-green-50 border border-green-200 rounded-lg">
      <div className="flex items-center justify-center">
        <div className="flex-shrink-0">
          <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-green-800 font-medium">Aluno excluído com sucesso!</p>
          <p className="text-green-600 text-sm mt-1">Redirecionando para a lista de alunos...</p>
        </div>
      </div>
    </div>
  );
  
  if (!form) return (
    <div className="max-w-lg mx-auto mt-10 p-6 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-center justify-center">
        <div className="flex-shrink-0">
          <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-red-800 font-medium">
            {loadError ? 'Não foi possível abrir este aluno' : 'Aluno não encontrado'}
          </p>
          <p className="text-red-600 text-sm mt-1">
            {loadError || 'O aluno que você está tentando acessar não existe ou foi removido.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/alunos')}
            className="mt-3 px-4 py-2 bg-white border border-red-300 text-red-700 rounded hover:bg-red-100"
          >
            Voltar para a lista de alunos
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Editar Aluno</h2>
      
      {/* Mensagens de Feedback */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
            <div className="ml-auto">
              <button 
                onClick={() => setError('')}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-green-800 font-medium">{successMessage || 'Aluno atualizado com sucesso!'}</p>
              <p className="text-green-600 text-sm mt-1">Redirecionando para a lista de alunos...</p>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Dados Pessoais e Foto */}
        <div>
          <h3 className="font-bold text-lg mb-4 text-green-700">Dados Pessoais</h3>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Dados Pessoais - 8 colunas */}
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nome *</label>
                  <input 
                    type="text" 
                    name="nome" 
                    value={form.nome || ''} 
                    onChange={handleChange} 
                    maxLength={100}
                    className={getFieldClass('nome', fieldErrors)}
                    placeholder="Nome completo" 
                  />
                  {fieldErrors.nome && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.nome}</p>
                  )}
                </div>
                <div className="flex items-center mt-6">
                  <input type="checkbox" name="estrangeira" checked={form.estrangeira} onChange={handleChange} className="mr-2" />
                  <label className="text-sm">Estrangeiro</label>
                </div>
                {!form.estrangeira ? (
                  <div>
                    <label className="block text-sm font-medium mb-1">CPF *</label>
                    <input 
                      type="text" 
                      name="cpf" 
                      value={form.cpf || ''} 
                      onChange={handleChange} 
                      maxLength={14}
                      className={getFieldClass('cpf', fieldErrors)}
                      placeholder="000.000.000-00" 
                    />
                    {fieldErrors.cpf && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.cpf}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-1">Documento Estrangeiro *</label>
                    <input 
                      type="text" 
                      name="docEstrangeiro" 
                      value={form.docEstrangeiro || ''} 
                      onChange={handleChange} 
                      maxLength={50}
                      className={getFieldClass('docEstrangeiro', fieldErrors)}
                      placeholder="Documento de identificação" 
                    />
                    {fieldErrors.docEstrangeiro && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.docEstrangeiro}</p>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">Data de Nascimento *</label>
                  <input 
                    type="date" 
                    name="dataNascimento" 
                    value={form.dataNascimento || ''} 
                    onChange={handleChange}
                    className={getFieldClass('dataNascimento', fieldErrors)}
                  />
                  {fieldErrors.dataNascimento && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.dataNascimento}</p>
                  )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Naturalidade (Cidade) *</label>
              <input 
                type="text" 
                name="naturalidade" 
                value={form.naturalidade || ''} 
                onChange={handleChange} 
                maxLength={100}
                className={getFieldClass('naturalidade', fieldErrors)}
                placeholder="Cidade de nascimento" 
              />
              {fieldErrors.naturalidade && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.naturalidade}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input 
                type="email" 
                name="email" 
                value={form.email || ''} 
                onChange={handleChange} 
                maxLength={60}
                className={getFieldClass('email', fieldErrors)}
                placeholder="Email" 
              />
              {fieldErrors.email && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Celular</label>
              <input type="text" name="telefoneCelular" value={form.telefoneCelular} onChange={handleChange} maxLength={15} className={getFieldClass('telefoneCelular', fieldErrors)} placeholder="(00)00000-0000" />
              {fieldErrors.telefoneCelular && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.telefoneCelular}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Residencial</label>
              <input type="text" name="telefoneResidencial" value={form.telefoneResidencial} onChange={handleChange} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)0000-0000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Comercial</label>
              <input type="text" name="telefoneComercial" value={form.telefoneComercial} onChange={handleChange} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)0000-0000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Escolaridade</label>
              <select 
                name="escolaridade" 
                value={form.escolaridade || ''} 
                onChange={handleChange} 
                className={getFieldClass('escolaridade', fieldErrors)}
              >
                <option value="">Selecione a escolaridade</option>
                <option value="Primária">Primária</option>
                <option value="Fundamental Incompleto">Fundamental Incompleto</option>
                <option value="Fundamental Completo">Fundamental Completo</option>
                <option value="Ensino Médio Incompleto">Ensino Médio Incompleto</option>
                <option value="Ensino Médio Completo">Ensino Médio Completo</option>
                <option value="Ensino Superior Incompleto">Ensino Superior Incompleto</option>
                <option value="Ensino Superior Completo">Ensino Superior Completo</option>
                <option value="Mestrado">Mestrado</option>
                <option value="Doutorado">Doutorado</option>
              </select>
              {fieldErrors.escolaridade && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.escolaridade}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado Civil</label>
              <select name="estadoCivil" value={form.estadoCivil} onChange={handleChange} className={getFieldClass('estadoCivil', fieldErrors)}>
                <option value="">Selecione o estado civil</option>
                <option value="Casado(a)">Casado(a)</option>
                <option value="Solteiro(a)">Solteiro(a)</option>
                <option value="Divorciado(a)">Divorciado(a)</option>
                <option value="Viúvo(a)">Viúvo(a)</option>
              </select>
              {fieldErrors.estadoCivil && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.estadoCivil}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Filiação Mãe</label>
              <input type="text" name="filiacaoMae" value={form.filiacaoMae} onChange={handleChange} maxLength={100} className={getFieldClass('filiacaoMae', fieldErrors)} placeholder="Nome da mãe" />
              {fieldErrors.filiacaoMae && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.filiacaoMae}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Filiação Pai</label>
              <input type="text" name="filiacaoPai" value={form.filiacaoPai} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Nome do pai" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Identidade</label>
              <input type="text" name="identidade" value={form.identidade} onChange={handleChange} maxLength={20} className={getFieldClass('identidade', fieldErrors)} placeholder="Identidade" />
              {fieldErrors.identidade && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.identidade}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Órgão Expedidor</label>
              <input type="text" name="orgaoExpedidor" value={form.orgaoExpedidor} onChange={handleChange} maxLength={30} className={getFieldClass('orgaoExpedidor', fieldErrors)} placeholder="Órgão Expedidor" />
              {fieldErrors.orgaoExpedidor && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.orgaoExpedidor}</p>
              )}
            </div>
              </div>
            </div>
            
            {/* Foto - 4 colunas */}
            <div className="lg:col-span-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 h-full flex flex-col items-center justify-center">
                <FotoUpload
                  currentPhotoUrl={currentFotoUrl}
                  onPhotoChange={(file) => setFotoFile(file)}
                  onPhotoRemove={async () => {
                    try {
                      await fotoService.removeFoto(id);
                      setCurrentFotoUrl(null);
                      setFotoFile(null);
                    } catch (error) {
                      console.error('Erro ao remover foto:', error);
                    }
                  }}
                  label="Foto do Aluno"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Dados Acadêmicos */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Dados Acadêmicos</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Cargo</label>
              <select name="cargo" value={form.cargo} onChange={handleChange} className={getFieldClass('cargo', fieldErrors)}>
                <option value="">Selecione o cargo</option>
                <option value="Pastor(a)">Pastor(a)</option>
                <option value="Missionário(a)">Missionário(a)</option>
                <option value="Evangelista">Evangelista</option>
                <option value="Presbítero">Presbítero</option>
                <option value="Diácono(nisa)">Diácono(nisa)</option>
                <option value="Aux. de Trabalho">Aux. de Trabalho</option>
                <option value="Membro">Membro</option>
                <option value="Líder de Dpto">Líder de Dpto</option>
                <option value="Congregado">Congregado</option>
              </select>
              {fieldErrors.cargo && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.cargo}</p>
              )}
            </div>
            {nivelAcesso > 3 && (
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select name="status" value={form.status} onChange={handleChange} className="w-full border rounded px-3 py-2">
                  <option value="">Selecione o status</option>
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                  <option value="Concluído">Concluído</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Extensão *</label>
              <select 
                name="extensao" 
                value={form.extensao || ''} 
                onChange={handleChange} 
                className={getFieldClass('extensao', fieldErrors)}
                disabled={loadingLists}
              >
                <option value="">
                  {loadingLists ? 'Carregando extensões...' : 'Selecione uma extensão'}
                </option>
                {extensoes.map(extensao => (
                  <option key={extensao.idExtensao} value={extensao.idExtensao}>
                    {extensao.nome}
                  </option>
                ))}
              </select>
              {fieldErrors.extensao && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.extensao}</p>
              )}
            </div>
            {/* Matrícula e Data de Matrícula - somente para nível >= 4 */}
            {nivelAcesso >= 4 && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Número de Matrícula</label>
                  <input
                    type="text"
                    name="matricula"
                    value={form.matricula || ''}
                    onChange={handleChange}
                    maxLength={50}
                    className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${matriculaEmUso ? 'border-red-500 bg-red-50' : ''}`}
                    placeholder="Número de matrícula"
                    onBlur={async (e) => {
                      const valor = e.target.value.trim();
                      if (!valor) { setMatriculaEmUso(false); setMatriculaEmUsoNome(''); return; }
                      setCheckingMatricula(true);
                      const resultado = await checkMatricula(valor, id);
                      setMatriculaEmUso(resultado.emUso || false);
                      setMatriculaEmUsoNome(resultado.aluno?.nome || '');
                      setCheckingMatricula(false);
                    }}
                  />
                  {checkingMatricula && (
                    <p className="text-gray-500 text-xs mt-1">Verificando...</p>
                  )}
                  {matriculaEmUso && (
                    <p className="text-red-600 text-xs mt-1">
                      Esta matrícula já está em uso pelo aluno: <strong>{matriculaEmUsoNome}</strong>
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data de Matrícula</label>
                  <input
                    type="date"
                    name="dataMatricula"
                    value={form.dataMatricula || ''}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {/* Endereço */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Endereço</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Bairro</label>
              <input type="text" name="bairro" value={form.bairro} onChange={handleChange} maxLength={50} className={getFieldClass('bairro', fieldErrors)} placeholder="Bairro" />
              {fieldErrors.bairro && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.bairro}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cidade</label>
              <input type="text" name="cidade" value={form.cidade} onChange={handleChange} maxLength={100} className={getFieldClass('cidade', fieldErrors)} placeholder="Cidade" />
              {fieldErrors.cidade && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.cidade}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <input type="text" name="estado" value={form.estado} onChange={handleChange} maxLength={2} className={getFieldClass('estado', fieldErrors)} placeholder="UF" />
              {fieldErrors.estado && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.estado}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CEP</label>
              <input type="text" name="cep" value={form.cep} onChange={handleChange} maxLength={10} className={getFieldClass('cep', fieldErrors)} placeholder="12345-678" />
              {fieldErrors.cep && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.cep}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Logradouro</label>
              <input type="text" name="logradouro" value={form.logradouro} onChange={handleChange} maxLength={100} className={getFieldClass('logradouro', fieldErrors)} placeholder="Logradouro" />
              {fieldErrors.logradouro && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.logradouro}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Número</label>
              <input type="text" name="numero" value={form.numero} onChange={handleChange} maxLength={30} className={getFieldClass('numero', fieldErrors)} placeholder="Número" />
              {fieldErrors.numero && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.numero}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Complemento</label>
              <input type="text" name="complemento" value={form.complemento} onChange={handleChange} maxLength={255} className="w-full border rounded px-3 py-2" placeholder="Complemento" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <input type="text" name="pais" value={form.pais} onChange={handleChange} maxLength={50} className={getFieldClass('pais', fieldErrors)} placeholder="País" />
              {fieldErrors.pais && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.pais}</p>
              )}
            </div>
          </div>
        </div>
        {/* Igreja */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Igreja</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome Igreja</label>
              <input type="text" name="nomeIgrejaCongrega" value={form.nomeIgrejaCongrega} onChange={handleChange} maxLength={100} className={getFieldClass('nomeIgrejaCongrega', fieldErrors)} placeholder="Nome da Igreja" />
              {fieldErrors.nomeIgrejaCongrega && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.nomeIgrejaCongrega}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nome Pastor</label>
              <input type="text" name="nomePastor" value={form.nomePastor} onChange={handleChange} maxLength={100} className={getFieldClass('nomePastor', fieldErrors)} placeholder="Nome do Pastor" />
              {fieldErrors.nomePastor && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.nomePastor}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <input type="text" name="paisIgreja" value={form.paisIgreja} onChange={handleChange} maxLength={50} className={getFieldClass('paisIgreja', fieldErrors)} placeholder="País da Igreja" />
              {fieldErrors.paisIgreja && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.paisIgreja}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Upload de Documentos */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Upload de Documentos</h3>
          <p className="text-sm text-gray-600 mb-4">
            Envie os documentos do aluno em formato PDF ou imagem (JPG, PNG).
            {nivelAcesso <= 3 && (
              <span className="text-yellow-600 ml-1">
                Os documentos enviados ficarão aguardando aprovação.
              </span>
            )}
          </p>
          <DocumentoUpload 
            alunoId={id} 
            nivelAcesso={nivelAcesso}
            readOnly={false}
          />
        </div>
        
        {/* Documentos Entregues - Visível apenas para nível de acesso > 3 */}
        {nivelAcesso > 3 && (
          <div>
            <h3 className="font-bold text-lg mb-2 text-green-700">Documentos Entregues (Checklist)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center">
                <input type="checkbox" name="docComprovanteResidencia" checked={form.docComprovanteResidencia} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Comprovante de Residência</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="docHistoricoEscolar" checked={form.docHistoricoEscolar} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Histórico Escolar</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="docIdentidade" checked={form.docIdentidade} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Identidade</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="docCpf" checked={form.docCpf} onChange={handleChange} className="mr-2" />
                <label className="text-sm">CPF</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="docFoto" checked={form.docFoto} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Foto 3x4</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="docCertidaoCasamentoNascimento" checked={form.docCertidaoCasamentoNascimento} onChange={handleChange} className="mr-2" />
                <label className="text-sm">Certidão de nascimento/casamento</label>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex gap-4 mt-6 justify-end">
          {hasPermission('alunos.audit') && (
            <button
              type="button"
              className="px-4 py-2 rounded bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 focus:outline-none flex items-center gap-2"
              onClick={handleOpenHistorico}
             aria-label="Histórico">
              <FontAwesomeIcon icon={faHistory} />
              <span className="hidden sm:inline">Histórico</span>
            </button>
          )}
          <button
            type="button"
            className="px-4 py-2 rounded bg-gray-400 text-white font-semibold shadow hover:bg-gray-500 focus:outline-none flex items-center gap-2"
            onClick={() => navigate('/alunos')}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button
            type="submit"
            className={`px-4 py-2 rounded font-semibold shadow focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2 ${
              loading 
                ? 'bg-green-400 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
            disabled={loading}
           aria-label="Salvar Alterações">
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="hidden sm:inline">Salvando...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span className="hidden sm:inline">Salvar Alterações</span>
              </>
            )}
          </button>
          {hasPermission('alunos.delete') && (
          <button
            type="button"
            className={`px-4 py-2 rounded font-semibold shadow focus:outline-none flex items-center gap-2 ${
              deleting
                ? 'bg-red-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
            onClick={handleDelete}
            disabled={deleting || loading}
           aria-label="Excluir Aluno">
            {deleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="hidden sm:inline">Excluindo...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} />
                <span className="hidden sm:inline">Excluir Aluno</span>
              </>
            )}
          </button>
          )}
        </div>
      </form>
      <ConfirmModal
        isOpen={showConfirm}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        title="Confirmar Alterações"
        message={`Tem certeza que deseja salvar as alterações do aluno "${form?.nome}"?`}
      />
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o aluno "${form?.nome}"? Esta ação não pode ser desfeita.`}
        confirmType="delete"
      />

      {/* Modal de Histórico de Alterações */}
      {showHistorico && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faHistory} className="text-blue-600" />
                Histórico de Alterações
              </h3>
              <button
                onClick={() => setShowHistorico(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {historicoLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                  <span className="text-gray-500">Carregando histórico...</span>
                </div>
              ) : historico.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FontAwesomeIcon icon={faHistory} className="text-4xl mb-3 text-gray-300" />
                  <p>Nenhuma alteração registrada para este aluno.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historico.map((item, index) => (
                    <div key={item.id || index} className="border rounded-lg p-4 bg-gray-50">
                      {/* Cabeçalho do registro */}
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-bold text-white ${
                          item.acao === 'CREATE' ? 'bg-green-500' :
                          item.acao === 'UPDATE' ? 'bg-blue-500' :
                          item.acao === 'DELETE' ? 'bg-red-500' : 'bg-gray-500'
                        }`}>
                          {item.acao === 'CREATE' ? 'Cadastro' :
                           item.acao === 'UPDATE' ? 'Alteração' :
                           item.acao === 'DELETE' ? 'Exclusão' : item.acao}
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date(item.data).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      
                      {/* Quem fez */}
                      <p className="text-sm text-gray-600 mb-2">
                        Por: <strong>{item.usuario?.nome || 'Sistema'}</strong>
                        {item.ip && <span className="text-gray-400 ml-2">(IP: {item.ip})</span>}
                      </p>

                      {/* Campos alterados (UPDATE) */}
                      {item.acao === 'UPDATE' && item.camposAlterados && item.camposAlterados.length > 0 && (
                        <div className="mt-2">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="bg-gray-200">
                                <th className="text-left p-2 rounded-tl">Campo</th>
                                <th className="text-left p-2">Valor Anterior</th>
                                <th className="text-left p-2 rounded-tr">Novo Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.camposAlterados.map((campo, ci) => (
                                <tr key={ci} className="border-b border-gray-200">
                                  <td className="p-2 font-medium text-gray-700">
                                    {campoLabels[campo.campo] || campo.campo}
                                  </td>
                                  <td className="p-2 text-red-600">
                                    {campo.valorAnterior === null || campo.valorAnterior === '' ? (
                                      <span className="text-gray-400 italic">vazio</span>
                                    ) : String(campo.valorAnterior)}
                                  </td>
                                  <td className="p-2 text-green-600">
                                    {campo.valorNovo === null || campo.valorNovo === '' ? (
                                      <span className="text-gray-400 italic">vazio</span>
                                    ) : String(campo.valorNovo)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {item.acao === 'UPDATE' && (!item.camposAlterados || item.camposAlterados.length === 0) && (
                        <p className="text-sm text-gray-400 italic mt-1">Nenhum campo foi alterado visivelmente.</p>
                      )}

                      {/* Cadastro (CREATE) */}
                      {item.acao === 'CREATE' && (
                        <p className="text-sm text-green-700 mt-1">Aluno cadastrado no sistema.</p>
                      )}

                      {/* Exclusão (DELETE) */}
                      {item.acao === 'DELETE' && (
                        <p className="text-sm text-red-700 mt-1">Aluno excluído do sistema.</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Paginação */}
            {historicoPagination && historicoPagination.totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t bg-gray-50">
                <span className="text-sm text-gray-500">
                  Página {historicoPagination.page} de {historicoPagination.totalPages} ({historicoPagination.total} registros)
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadHistorico(historicoPagination.page - 1)}
                    disabled={historicoPagination.page <= 1}
                    className="px-3 py-1 text-sm rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => loadHistorico(historicoPagination.page + 1)}
                    disabled={historicoPagination.page >= historicoPagination.totalPages}
                    className="px-3 py-1 text-sm rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end p-4 border-t">
              <button
                onClick={() => setShowHistorico(false)}
                className="px-4 py-2 rounded bg-gray-400 text-white font-semibold hover:bg-gray-500"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlunoEdit;
